// ButtonBlock.tsx
import React, { useEffect, useState } from 'react';
import { LucideSquareArrowOutUpRight, ArrowLeft, Hourglass } from 'lucide-react';

interface ButtonBlockProps {
    button: {
        target?: string;
        title: string;
        url: string;
    };
    isLastStep?: boolean;
}

const Countdown24h: React.FC = () => {
    const [timeLeft, setTimeLeft] = useState<{ hours: string; minutes: string; seconds: string }>({
        hours: '23',
        minutes: '59',
        seconds: '59',
    });

    useEffect(() => {
        const STORAGE_KEY = 'survey_webinar_timer_end';
        let endTime = 0;
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = parseInt(saved, 10);
                if (!isNaN(parsed) && parsed > Date.now()) {
                    endTime = parsed;
                }
            }
            if (!endTime) {
                endTime = Date.now() + 24 * 60 * 60 * 1000;
                localStorage.setItem(STORAGE_KEY, endTime.toString());
            }
        } catch {
            endTime = Date.now() + 24 * 60 * 60 * 1000;
        }

        const updateTimer = () => {
            const now = Date.now();
            const diff = endTime - now;
            if (diff <= 0) {
                setTimeLeft({ hours: '00', minutes: '00', seconds: '00' });
            } else {
                const hrs = Math.floor((diff / (1000 * 60 * 60)) % 24);
                const mins = Math.floor((diff / (1000 * 60)) % 60);
                const secs = Math.floor((diff / 1000) % 60);
                setTimeLeft({
                    hours: String(hrs).padStart(2, '0'),
                    minutes: String(mins).padStart(2, '0'),
                    seconds: String(secs).padStart(2, '0'),
                });
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className='flex items-center justify-center gap-1.5 text-xs text-amber-800 dark:text-[#e7c282] font-semibold bg-amber-500/10 dark:bg-amber-400/10 py-1.5 px-3.5 rounded-full border border-amber-500/25 dark:border-[#e7c282]/25 mt-2.5 select-none transition-colors'>
            <Hourglass className='size-3.5 shrink-0 text-amber-600 dark:text-[#e7c282] animate-pulse' />
            <span>مهلت دسترسی ۲۴ ساعته:</span>
            <span
                className='font-mono font-bold tracking-wider text-gray-900 dark:text-white'
                dir='ltr'
            >
                {timeLeft.hours}:{timeLeft.minutes}:{timeLeft.seconds}
            </span>
        </div>
    );
};

const ButtonBlock: React.FC<ButtonBlockProps> = ({ button, isLastStep }) => {
    if (!button || !button.url) return null;

    if (isLastStep) {
        return (
            <div className='w-full flex flex-col items-center !mt-4 md:!mt-5'>
                <div className='relative w-full group'>
                    {/* Breathing Ambient Glow behind the button */}
                    <div className='absolute -inset-0.5 bg-gradient-to-r from-[#F6D388] via-[#E2A746] to-[#C08323] rounded-2xl blur-md opacity-60 group-hover:opacity-95 animate-pulse transition duration-500' />

                    <a
                        href={button.url}
                        target={button.target || '_blank'}
                        rel='noopener noreferrer'
                        className='relative w-full overflow-hidden py-3.5 px-4 sm:px-5 bg-gradient-to-r from-[#F8DB98] via-[#E5AC4B] to-[#C68725] hover:from-[#FAE1A4] hover:to-[#B87A1E] text-zinc-950 font-black rounded-2xl shadow-lg active:scale-[0.985] transition-all duration-300 flex items-center justify-between'
                    >
                        {/* Shimmer light sweep on hover */}
                        <span className='absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-[300%] transition-transform duration-1000 ease-out pointer-events-none' />

                        {/* Spacer for visual symmetry */}
                        <div className='w-8 shrink-0' />

                        {/* Centered button text */}
                        <span className='text-sm sm:text-base font-black tracking-tight text-center flex-1 px-2'>
                            {button.title}
                        </span>

                        {/* Animated Directional Arrow */}
                        <div className='size-8 rounded-full bg-black/10 dark:bg-black/15 flex items-center justify-center shrink-0 group-hover:-translate-x-1 transition-transform duration-300 shadow-sm'>
                            <ArrowLeft size={18} className='text-zinc-950 stroke-[2.5]' />
                        </div>
                    </a>
                </div>
                <Countdown24h />
            </div>
        );
    }

    return (
        <a
            href={button.url}
            target={button.target}
            className='bg-gray-50 dark:bg-white/5 flex justify-between items-center text-sm gap-2 py-3 lg:py-3.5 px-6 border border-gray-300 dark:border-gray-700 text-[#C28C38] font-bold rounded-xl w-full text-center transition-all !mt-2'
        >
            <LucideSquareArrowOutUpRight size={18} />
            <span className='text-black dark:text-white'>{button.title}</span>
            <ArrowLeft size={18} />
        </a>
    );
};

export default ButtonBlock;
